import type { HotelListOptions } from '@bandido/shared/types/hotels/hotel-search-results';
import { client as siteClient } from '@frontend/features/common/services';
import type { HotelSearchFilters } from '@bandido/shared/types/hotels/hotel-search-filters';
import { normalizedHotelFilters } from './searchModel';
import type { HotelSearchTransport } from './hotelSearchFlow';
import type { HotelSearchState, HotelSummary } from './searchModel';

export function createHotelSearchTransport(agencyId?: number): HotelSearchTransport {
  const headers = agencyId === undefined ? undefined : { 'Resource-Agency-Id': String(agencyId) };
  const client: Pick<typeof siteClient, 'get' | 'post'> = {
    get: (url, config) => siteClient.get(url, headers ? { ...config, headers } : config),
    post: (url, data, config) => siteClient.post(url, data, headers ? { ...config, headers } : config),
  };
  const transport: HotelSearchTransport = {
    async expansion(id, hotel, signal, create = false) {
      const path = `hotels/searches/${encodeURIComponent(id)}/hotels/${encodeURIComponent(hotel)}/expansion`;
      return (create ? await client.post(path, undefined, { signal }) : await client.get(path, { signal })).data.data;
    },
    async contentSummaries(ids, language, signal) {
      return (await client.post('hotels/catalog/content/summaries', { hotel_ids: ids, ...language }, { signal })).data;
    },
    async content(id, language, signal) {
      return (await client.get(`hotels/catalog/${encodeURIComponent(id)}/content`, { params: language, signal })).data
        .data;
    },
    async resolve(draft, _offset, signal) {
      const place = draft.location;
      if (!place) throw new Error('Missing hotel location');
      const location =
        place.type === 'destination'
          ? { type: 'destination', source: 'ubikeishon', place_id: place.placeId }
          : { type: 'point', latitude: place.latitude, longitude: place.longitude, radius_km: draft.radiusKm };
      const response = await client.post<{
        data: HotelSummary[];
        search_context_id: string | null;
        meta: { has_more: boolean; offset: number; limit: number };
      }>(
        'hotels/catalog/resolve',
        {
          location,
          limit: 200,
          offset: 0,
          ...(place.type === 'point' && (place.placeId || place.resolutionToken)
            ? {
                location_reference: place.placeId
                  ? { place_id: place.placeId }
                  : { resolution_token: place.resolutionToken },
              }
            : {}),
        },
        { signal },
      );
      const { data, meta } = response.data;
      const next = meta.offset + meta.limit;
      const searchContextId = response.data.search_context_id;
      if (data.length && (typeof searchContextId !== 'string' || !searchContextId.trim()))
        throw new Error('HOTEL_SEARCH_CONTEXT_REQUIRED');
      return {
        hotels: data,
        nextOffset: meta.has_more && next <= 10000 ? next : null,
        searchContextId: searchContextId ?? null,
      };
    },
    async create(criteria, signal) {
      return (await client.post<{ data: HotelSearchState }>('hotels/searches', criteria, { signal })).data.data;
    },
    async status(id, signal) {
      return (await client.get<{ data: HotelSearchState }>(`hotels/searches/${encodeURIComponent(id)}`, { signal }))
        .data.data;
    },
    async hotels(id, signal, filters, cursor, options) {
      return readResultPage(
        `hotels/searches/${encodeURIComponent(id)}/hotels`,
        signal,
        filters,
        cursor,
        undefined,
        options,
      );
    },
    async groups(id, hotel, signal, filters, cursor) {
      return readResultPage(
        `hotels/searches/${encodeURIComponent(id)}/hotels/${encodeURIComponent(hotel)}/groups`,
        signal,
        filters,
        cursor,
      );
    },
    async hotelFares(id, hotel, signal, filters, cursor, group) {
      return readResultPage(
        `hotels/searches/${encodeURIComponent(id)}/hotels/${encodeURIComponent(hotel)}/fares`,
        signal,
        filters,
        cursor,
        group,
      );
    },
  };
  async function readResultPage(
    path: string,
    signal: AbortSignal,
    filters?: HotelSearchFilters,
    cursor?: string,
    group?: string,
    options?: HotelListOptions,
  ) {
    const normalized = normalizedHotelFilters(filters);
    const params: Record<string, string | number> = {
      limit: 20,
      include_package_rates: String(normalized.include_package_rates ?? true),
    };
    if (normalized.board_categories.length) params.board_categories = normalized.board_categories.join(',');
    if (normalized.refundability) params.refundability = normalized.refundability;
    for (const key of ['sort', 'name', 'currency', 'price_basis'] as const) {
      if (options?.[key]) params[key] = options[key];
    }
    if (cursor) params.cursor = cursor;
    if (group) params.group_id = group;
    return (await client.get(path, { signal, params })).data;
  }

  return transport;
}
export const hotelSearchTransport = createHotelSearchTransport();
